import React, { useCallback, useState } from "react";

import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Button, Drawer, IconButton, Typography } from "@mui/material";

interface LogoDrawerProps {
  open: boolean;
  currentLogoUrl: string | null;
  onClose: () => void;
  onApply: (url: string | null, file: File | null) => void;
  onRemove: () => void;
}

export const LogoDrawer: React.FC<LogoDrawerProps> = ({ open, currentLogoUrl, onClose, onApply, onRemove }) => {
  const [tempLogoUrl, setTempLogoUrl] = useState(currentLogoUrl || "");
  const [tempLogoFile, setTempLogoFile] = useState<File | null>(null);

  // Reset state when drawer opens
  React.useEffect(() => {
    if (open) {
      setTempLogoUrl(currentLogoUrl || "");
      setTempLogoFile(null);
    }
  }, [open, currentLogoUrl]);

  const handleApply = useCallback(() => {
    onApply(tempLogoUrl || null, tempLogoFile);
  }, [tempLogoUrl, tempLogoFile, onApply]);

  const handleRemove = useCallback(() => {
    setTempLogoUrl("");
    setTempLogoFile(null);
    onRemove();
  }, [onRemove]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 400,
          borderTopRightRadius: "25px",
          borderBottomRightRadius: "25px"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "white" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "16px",
            bgcolor: "#edf1f9",
            color: "#0a3142"
          }}
        >
          <Typography sx={{ fontSize: "17px", fontWeight: 500 }}>
            {currentLogoUrl ? "Change Logo" : "Add Logo"}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#0a3142",
              "&:hover": { transform: "scale(1.1)" },
              transition: "transform 0.2s"
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Body */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, p: 3, overflow: "auto" }}>
          {/* Description */}
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Upload a logo image from your computer. The logo will appear before the report title in the header.
          </Typography>
          {/* File upload */}
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<AddPhotoAlternateIcon />}
            sx={{
              borderRadius: "8px",
              py: 1.5,
              borderStyle: "dashed"
            }}
          >
            Upload from computer
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setTempLogoFile(file);
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setTempLogoUrl(event.target?.result as string);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </Button>
          {/* Preview with hover overlay for remove */}
          {tempLogoUrl && (
            <Box>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
                Preview
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: 120,
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #e0e0e0",
                  bgcolor: "#f8fafc",
                  "&:hover .remove-overlay": {
                    opacity: 1
                  }
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Box
                    component="img"
                    src={tempLogoUrl}
                    alt="Logo preview"
                    sx={{
                      maxHeight: 100,
                      maxWidth: "90%",
                      objectFit: "contain"
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </Box>
                {/* Remove overlay - only show if there's a current logo */}
                {currentLogoUrl && (
                  <Box
                    className="remove-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: "rgba(0, 0, 0, 0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.2s ease-in-out",
                      cursor: "pointer"
                    }}
                    onClick={handleRemove}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5
                      }}
                    >
                      <IconButton
                        sx={{
                          bgcolor: "white",
                          "&:hover": {
                            bgcolor: "#f8fafc"
                          }
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                      <Typography variant="caption" sx={{ color: "white", fontWeight: 500 }}>
                        Remove logo
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            px: 3,
            py: 2,
            boxShadow: "0 -1px 5px 1px rgba(0, 0, 0, 0.08)"
          }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={!tempLogoUrl}>
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
